import { Box, Flex, Text } from '@chakra-ui/react'
import {
  AttachmentShortcutFontStyle,
  AttachmentShortcutStyle,
  defaultFont,
  FontId,
  fontSupportsBold,
  fontSupportsItalic,
} from '@hb/shared'
import { useCallback, useMemo } from 'react'
import { ColorSelect } from '../../ColorSelect'
import { useExternalPdfTemplateEditor } from '../../Templates/ExternalPdf/context'
import { FontSelect } from '../fonts/FontSelect'
import { FontSizeSelect, StyleToggle } from '../ToolbarComponents'

export const AttachmentStyleToolbar: React.FC = () => {
  const { selectedAttachmentId, externalPdf, updateAttachment } = useExternalPdfTemplateEditor()

  const attachment = selectedAttachmentId ? externalPdf.attachments[selectedAttachmentId] : null
  const dataAttachment = attachment?.type === 'data' ? attachment : null
  const style = useMemo(() => dataAttachment?.shortcut.style ?? {}, [dataAttachment])
  const font = useMemo(() => style.font ?? {}, [style])

  const updateStyle = useCallback(
    (patch: Partial<AttachmentShortcutStyle>) => {
      if (!selectedAttachmentId || !dataAttachment) return
      updateAttachment(selectedAttachmentId, {
        ...dataAttachment,
        shortcut: { ...dataAttachment.shortcut, style: { ...style, ...patch } },
      })
    },
    [selectedAttachmentId, dataAttachment, style, updateAttachment],
  )

  const updateFont = useCallback(
    (patch: Partial<AttachmentShortcutFontStyle>) => {
      updateStyle({ font: { ...font, ...patch } })
    },
    [updateStyle, font],
  )

  const setFamily = useCallback(
    (family: FontId) => {
      updateFont({
        family,
        bold: fontSupportsBold(family) ? font.bold : undefined,
        italic: fontSupportsItalic(family) ? font.italic : undefined,
      })
    },
    [updateFont, font],
  )

  const family = font.family ?? defaultFont.id
  const canBold = fontSupportsBold(family)
  const canItalic = fontSupportsItalic(family)

  if (!dataAttachment) return null

  return (
    <Flex w="100%" align="center" px={2} gap={2} flexWrap="wrap">
      <Text fontWeight={600} fontSize="sm" flexShrink={0}>
        {dataAttachment.shortcut.name}
      </Text>
      <Flex align="center">
        <StyleToggle
          label="B"
          active={font.bold ?? false}
          disabled={!canBold}
          toggle={v => updateFont({ bold: v || undefined })}
        />
        <StyleToggle
          label="I"
          active={font.italic ?? false}
          disabled={!canItalic}
          toggle={v => updateFont({ italic: v || undefined })}
        />
        <StyleToggle
          label="U"
          active={font.underline ?? false}
          toggle={v => updateFont({ underline: v || undefined })}
        />
      </Flex>
      <FontSelect font={font.family} setFont={setFamily} />
      <FontSizeSelect
        fontSize={font.size ?? 1}
        setFontSize={size => updateFont({ size })}
        step={0.1}
      />
      <ColorSelect
        color={font.color}
        setColor={color => updateFont({ color: color ?? undefined })}
      />
      <Box w="1px" h="20px" bg="#cdcdcd" flexShrink={0} />
      <ColorSelect
        color={style.backgroundColor}
        setColor={bg => updateStyle({ backgroundColor: bg ?? undefined })}
      />
    </Flex>
  )
}
